const { Core, Utils, DOMEvents, Events, TopLayerStack, MediaQueries } = Global;
const { $fetch, $replaceContent, $active } = Utils;

const ENTER_KEY_IDENTIFIER = 'Enter';

const animationsComplete = element =>
  Promise.allSettled(
    element.getAnimations().map(animation => 
      animation.finished))

const waitAnimations = (element, timeoutMs = 400) =>
  Promise.race([
    animationsComplete(element),
    new Promise(resolve => setTimeout(resolve, timeoutMs))
  ])

const CLICK_THROUGH_SUPPRESS_MS = 450;
let clickThroughSuppressUntil = 0;
let clickThroughBlocker = null;

const ensureClickThroughBlocker = () => {
  if (clickThroughBlocker) {
    return;
  }

  clickThroughBlocker = (e) => {
    if (Date.now() > clickThroughSuppressUntil) {
      return;
    }
    e.preventDefault();
    e.stopImmediatePropagation();
  };

  document.addEventListener('click', clickThroughBlocker, true);
  document.addEventListener('touchend', clickThroughBlocker, true);
  document.addEventListener('pointerup', clickThroughBlocker, true);
};

const suppressClickThrough = (ms = CLICK_THROUGH_SUPPRESS_MS) => {
  clickThroughSuppressUntil = Date.now() + ms;
  ensureClickThroughBlocker();
};

customElements.define('modal-container', class extends Core {
    elements = {
        $: ['dialog', 'content', 'spinner']
    }

    propTypes = {
        'remote-url': String,
        'remote-target': String,
        'remote-detach': Boolean,
        // 'top-layer': Boolean,
    }

    render() {
        this.$('dialog', {
            click: this._handleBackdropClick
        })

        this.$({
            'modal-close': this._close,
            'modal-open': this._open
        })

        this._initSwipeDismiss();
    }

    _initSwipeDismiss() {
        const direction = this.getAttribute('swipe-dismiss');
        if (!direction) {
            return;
        }

        this._swipeDismissDirection = direction;
        this._swipeState = null;

        const dialog = this.$('dialog');
        dialog.addEventListener('touchstart', this._handleSwipeStart.bind(this), { passive: true });
        dialog.addEventListener('touchmove', this._handleSwipeMove.bind(this), { passive: false });
        dialog.addEventListener('touchend', this._handleSwipeEnd.bind(this), { passive: false });
        dialog.addEventListener('touchcancel', this._handleSwipeEnd.bind(this), { passive: false });
    }

    _canSwipeDismiss() {
        if (!MediaQueries.MOBILE.matches || !this.$('dialog').open) {
            return false;
        }

        const navMobile = this.querySelector('main-nav-mobile');
        if (navMobile) {
            const level = parseInt(getComputedStyle(navMobile).getPropertyValue('--main-nav-mobile-level'), 10) || 0;
            if (level > 0) {
                return false;
            }
        }

        return true;
    }

    _resetSwipeTransform() {
        const content = this.$('content');
        content.style.transition = '';
        content.style.transform = '';
        content.style.animation = '';
        this.removeAttribute('swipe-dragging');
        this.style.removeProperty('--modal-backdrop-opacity');
        this._swipeState = null;
    }

    _restoreFocusAfterClose() {
        const dialog = this.$('dialog');
        dialog.blur();

        const blurStrayFocus = () => {
            const active = document.activeElement;
            if (!active || active === document.body || active === document.documentElement) {
                return;
            }
            active.blur();
        };

        blurStrayFocus();
        requestAnimationFrame(blurStrayFocus);
        setTimeout(blurStrayFocus, 0);
    }

    _hasSwipeDismiss() {
        return !!this.getAttribute('swipe-dismiss');
    }

    _handleSwipeStart(e) {
        if (!this._canSwipeDismiss() || e.touches.length !== 1) {
            return;
        }

        const touch = e.touches[0];
        this._swipeState = {
            startX: touch.clientX,
            startY: touch.clientY,
            currentX: touch.clientX,
            currentY: touch.clientY,
            dragging: false,
            startTime: Date.now()
        };
    }

    _handleSwipeMove(e) {
        const state = this._swipeState;
        if (!state || !this._canSwipeDismiss()) {
            return;
        }

        const touch = e.touches[0];
        const dx = touch.clientX - state.startX;
        const dy = touch.clientY - state.startY;

        if (!state.dragging) {
            if (Math.abs(dy) > Math.abs(dx) && Math.abs(dy) > 10) {
                this._swipeState = null;
                return;
            }
            if (Math.abs(dx) <= 10) {
                return;
            }
            state.dragging = true;
            this.setAttribute('swipe-dragging', '');
        }

        const dismissOffset = this._swipeDismissDirection === 'left'
            ? Math.min(0, dx)
            : Math.max(0, dx);

        if (dismissOffset === 0) {
            return;
        }

        e.preventDefault();

        state.currentX = touch.clientX;
        state.currentY = touch.clientY;

        const content = this.$('content');
        content.style.animation = 'none';
        content.style.transform = `translate3d(${dismissOffset}px, 0, 0)`;

        const width = content.offsetWidth || 1;
        const progress = Math.min(1, Math.abs(dismissOffset) / width);
        this.style.setProperty('--modal-backdrop-opacity', String(1 - progress * 0.5));
    }

    _handleSwipeEnd(e) {
        const state = this._swipeState;
        if (!state?.dragging) {
            this._swipeState = null;
            return;
        }

        const dx = state.currentX - state.startX;
        const dismissOffset = this._swipeDismissDirection === 'left'
            ? Math.min(0, dx)
            : Math.max(0, dx);
        const width = this.$('content').offsetWidth || 1;
        const elapsed = Math.max(Date.now() - state.startTime, 1);
        const velocity = Math.abs(dismissOffset) / elapsed;
        const shouldClose = Math.abs(dismissOffset) > width * 0.28
            || (Math.abs(dismissOffset) > 48 && velocity > 0.45);

        this._swipeState = null;

        if (shouldClose) {
            e?.preventDefault();
            if (this._hasSwipeDismiss()) {
                suppressClickThrough();
            }
            this.close({ swipe: true });
            return;
        }

        const content = this.$('content');
        content.style.transition = 'transform 0.22s ease';
        content.style.transform = '';
        this.style.removeProperty('--modal-backdrop-opacity');
        this.removeAttribute('swipe-dragging');

        const onSnapBack = () => {
            content.removeEventListener('transitionend', onSnapBack);
            content.style.transition = '';
            content.style.animation = '';
        };
        content.addEventListener('transitionend', onSnapBack);
    }

    open() {
        // this.dispatchEvent(DOMEvents[this.prop('top-layer') ? 'MODAL_OPEN' : 'MODAL_SHOW']);
        this.dispatchEvent(DOMEvents.MODAL_OPEN);
    }

    close(options = {}) {
        this._closeOptions = options;
        this.dispatchEvent(DOMEvents.MODAL_CLOSE);
    }

    refreshLayer() {
        this.$('dialog').close();
        this.$('dialog').showModal();
    }

    _handleBackdropClick({ target }) {
        if(target === this.$('dialog')) {
            if (this._hasSwipeDismiss()) {
                suppressClickThrough();
            }
            this.dispatchEvent(DOMEvents.MODAL_CLOSE);
        }
    }
    
    async _loadRemoteContent() {
        // TODO: handle errors for user
        try {
            this.$remoteContent = await $fetch(this.prop('remote-url'), {
                before: this._loading(true),
                after: this._loading(false),
                select: this.prop('remote-target')
            });
        } catch(err) {
            console.error(err);
        }
    }

    _loading(state) {
        return () => {
            $active(this.$('spinner'), state);
        }
    }

    _attachRemoteContent() {
        if(this.$remoteContent) {
            this.$('content').append(this.$remoteContent);
        }
    }

    _detachRemoteContent() {
        if(this.$remoteContent && this.prop('remote-detach')) {
            this.$remoteContent.remove();
        }
    }

    async _handleRemoteContent() {
        if(this.prop('remote-url') && this.prop('remote-target') && !this.$remoteContent) {
            await this._loadRemoteContent();
        }

        this._attachRemoteContent();
    }
    
    _onClose(cb) {
        this.addEventListener('modal-close', cb, {once: true});
    }

    async _open(e) {
        if(this.$('dialog').open) {
            return;
        }
        this._resetSwipeTransform();
        TopLayerStack.add(this.$('dialog'), this._onClose.bind(this));        
        // this.$('dialog')[e.type === 'modal-open' ? 'showModal': 'show']();
        this.$('dialog').showModal();

        await this._handleRemoteContent();
        this.setAttribute('opening', '');

        await waitAnimations(this.$('content')); 
        this.removeAttribute('opening');

        this.$('dialog').dispatchEvent(new CustomEvent('modal-open', {
            bubbles: true
        }))
    }

    async _close(e) {
        e?.preventDefault();
        if(!this.$('dialog').open) {
            return;
        }

        const swipeDismissModal = this._hasSwipeDismiss();
        if (swipeDismissModal) {
            suppressClickThrough();
        }

        const swipeDismiss = this._closeOptions?.swipe === true;
        this._closeOptions = null;

        this._resetSwipeTransform();
        TopLayerStack.remove();
        this.setAttribute('closing', '');

        if (swipeDismiss) {
            const content = this.$('content');
            const width = content.offsetWidth || 1;
            const offset = this._swipeDismissDirection === 'left' ? -width : width;
            content.style.animation = 'none';
            content.style.transition = 'transform 0.22s ease';
            content.style.transform = `translate3d(${offset}px, 0, 0)`;
            await waitAnimations(content, 260);
        } else {
            await waitAnimations(this.$('content'));
        }

        this.$('dialog').close();
        this.removeAttribute('closing');
        if (swipeDismissModal) {
            this._restoreFocusAfterClose();
        }

        this._detachRemoteContent();
    }
})

customElements.define('modal-open', class extends Core {
    propTypes = {
        target: String,
        fallback: String
    }

    render() {
        this.$target = document
            .getElementById(this.prop('target'))

        this._handleClick();
    }

    async _handleClick() {
        await customElements.whenDefined('modal-container');

        this.$({
            click: this._openTargetModal,
            keydown: this._openOnEnterPressed
        })
    }

    _openOnEnterPressed(e) {
        if(e.key === ENTER_KEY_IDENTIFIER && e.target === this) {
            e.preventDefault();
            e.stopPropagation();
            this._openTargetModal()
        }
    }

    _openTargetModal() {
        if(!this.$target) {
            if(this.prop('fallback')) {
                window.location.href = this.prop('fallback');
            }
            return;
        }
        this.$target.dispatchEvent(DOMEvents.MODAL_OPEN);
    }
})

customElements.define('modal-close', class extends Core {
    render() {
        this.$({
            click: this._emitClose
        })
    }

    _emitClose() {
        this.dispatchEvent(DOMEvents.MODAL_CLOSE);
    }
})